import * as React from "react";
import { useCallback } from "react";
import { Download } from "lucide-react";
import type { Exporter } from "ra-core";
import {
  fetchRelatedRecords,
  useCanAccess,
  useDataProvider,
  useGetResourceLabel,
  useNotify,
  useListContext,
  useResourceTranslation,
} from "ra-core";
import { Button } from "@/components/ui/button";

/**
 * A button that exports list data to a file.
 *
 * Respects current filters and sort order, with configurable maximum results.
 * Use a custom exporter to customize the result and fetch related records.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/exportbutton/ ExportButton documentation}
 *
 * @example
 * import { CreateButton, ExportButton, TopToolbar } from '@/components/admin';
 *
 * const PostListActions = () => (
 *   <>
 *     <FilterButton />
 *     <CreateButton />
 *     <ExportButton />
 *   </>
 * );
 *
 * export const PostList = () => (
 *   <List actions={<PostListActions />}>
 *     ...
 *   </List>
 * );
 */
export const ExportButton = (props: ExportButtonProps) => {
  const { resource } = useListContext();
  const { canAccess, isPending } = useCanAccess({
    action: "export",
    resource,
  });
  // Hide the button when the role has no export access (Almikia Epic E3b,
  // owner answer A6): advisors and other non-export roles never see it.
  if (isPending || !canAccess) return null;
  return <ExportButtonView {...props} />;
};

const ExportButtonView = (props: ExportButtonProps) => {
  const {
    maxResults = 1000,
    onClick,
    label: labelProp,
    icon = defaultIcon,
    exporter: customExporter,
    meta,
    className = "cursor-pointer",
  } = props;
  const {
    getData,
    total,
    resource,
    exporter: exporterFromContext,
  } = useListContext();
  const getResourceLabel = useGetResourceLabel();
  const label = useResourceTranslation({
    resourceI18nKey: `resources.${resource}.action.export`,
    baseI18nKey: "ra.action.export",
    options: {
      name: getResourceLabel(resource, 1),
    },
    userText: labelProp,
  });
  const exporter = customExporter || exporterFromContext;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!getData) {
        throw new Error(
          "ListContext.getData must be defined to use ExportButton.",
        );
      }

      // Almikia Epic E3b (TASK-071): flag the read so the data provider
      // routes it through the server-side export path (TASK-066) instead of
      // a plain list read -- see ADR-ee2f59a9-TASK-066-server-side-export-enforcement.md.
      getData({ maxResults, meta: { ...meta, export: true } })
        .then(
          (data) =>
            exporter &&
            exporter(
              data,
              fetchRelatedRecords(dataProvider),
              dataProvider,
              resource,
            ),
        )
        .catch((error) => {
          console.error(error);
          notify("ra.notification.http_error", { type: "error" });
        });
      if (typeof onClick === "function") {
        onClick(event);
      }
    },
    [
      dataProvider,
      exporter,
      getData,
      notify,
      onClick,
      resource,
      maxResults,
      meta,
    ],
  );

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={total === 0}
      className={className}
    >
      {icon}
      {label}
    </Button>
  );
};

const defaultIcon = <Download />;

export interface ExportButtonProps {
  className?: string;
  exporter?: Exporter;
  icon?: React.ReactNode;
  label?: string;
  maxResults?: number;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  resource?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: any;
}
